import { IAdjacentValues, RangeConverter, Ranges } from "@cimpress-technology/numeric-ranges";
import TokenEngine from "@vp/vp-js-token-engine";
import Decimal from "decimal.js";
import RangeType from "../types/RangeTypes";
import { Translations } from "./Translation";
import {
  RangedAttributeValidationCode,
  RangedAttributeValidationResultAdditionalInformation,
} from "@vp/product-options-ui";
import { ProductBuilderSelection } from "../types/ProductBuilderTypes";

export const buildRangesType = (ranges: RangeType[]): Ranges | null => {
  if (ranges && ranges.length > 0) {
    return RangeConverter.toRanges(ranges);
  }

  return null;
};

export const getMaximumValue = (rangeInformation: Ranges) => {
  return rangeInformation
    .getRanges()
    .reduce((currentMax, currentRange) =>
      currentMax.getMaximum().greaterThan(currentRange.getMaximum()) ? currentMax : currentRange
    )
    .getMaximum();
};

export const getMinimumValue = (rangeInformation: Ranges) => {
  return rangeInformation
    .getRanges()
    .reduce((currentMin, currentRange) =>
      currentMin.getMinimum().lessThan(currentRange.getMinimum()) ? currentMin : currentRange
    )
    .getMinimum();
};

export const getTextBoxPadding = (
  selectionStyle: string,
  removeLeft: boolean,
  selection: ProductBuilderSelection
) => {
  return selectionStyle === "Text box" &&
    ["custom width", "custom height"].includes(selection?.property.key.toLowerCase())
    ? removeLeft
      ? "ui-density-update-left-textbox"
      : "ui-density-update-right-textbox"
    : "";
};

export const calculateColumnSpan = (selectionStyle: string, selection: ProductBuilderSelection) => {
  if (
    selectionStyle === "Text box" &&
    ["custom width", "custom height"].includes(selection?.property.key.toLowerCase())
  ) {
    return 6;
  }

  return 12;
};

export const getPaddingRequirement = (selectionStyle: string, current: boolean) => {
  return selectionStyle === "Text box" ? !current : false;
};

export const getHelperText = (minimum: Decimal, maximum?: Decimal, unitOfMeasure?: string) => {
  const translations = Translations.getInstance();
  const fullHelperTextEntry = translations.getTranslationEntry("fullRangedHelperText");
  const partialHelperTextEntry = translations.getTranslationEntry("partialRangedHelperText");
  const tokenEngine = new TokenEngine();

  let helperText;
  let usedEntry;
  if (fullHelperTextEntry && maximum) {
    helperText = tokenEngine.replace(fullHelperTextEntry, {
      Minimum: minimum,
      UnitOfMeasure: unitOfMeasure,
      Maximum: maximum,
    });
    usedEntry = fullHelperTextEntry;
  } else if (partialHelperTextEntry) {
    helperText = tokenEngine.replace(partialHelperTextEntry, {
      Minimum: minimum,
      UnitOfMeasure: unitOfMeasure,
    });
    usedEntry = partialHelperTextEntry;
  }

  return { helperText, usedEntry };
};

export const getValidationMessage = (props: {
  isConstrained: boolean;
  validationCode: RangedAttributeValidationCode | undefined;
  rangeInformation: Ranges;
  unitOfMeasure: string;
  additionalInformation?: RangedAttributeValidationResultAdditionalInformation;
}): string => {
  const {
    isConstrained,
    validationCode,
    additionalInformation,
    rangeInformation,
    unitOfMeasure,
  } = props;

  if (isConstrained) {
    const outOfStockButtonAttributeEntry = Translations.getInstance().getTranslationEntry(
      "outOfStockButtonAttribute"
    );
    return outOfStockButtonAttributeEntry;
  }

  switch (validationCode) {
    case RangedAttributeValidationCode.EMPTY_INPUT: {
      return getValidationMessageForRequiredField(
        getMinimumValue(rangeInformation),
        getMaximumValue(rangeInformation),
        unitOfMeasure
      );
    }
    case RangedAttributeValidationCode.SUCCESS: {
      return "";
    }
    case RangedAttributeValidationCode.NOT_IN_RANGE: {
      return getValidationMessageWithNearestNumber(
        additionalInformation?.adjacentValues,
        unitOfMeasure
      );
    }
    case RangedAttributeValidationCode.UNSUPPORTED_PRECISION: {
      return Translations.getInstance().getTranslationEntry("inputErrorPrecision");
    }
    case RangedAttributeValidationCode.NOT_A_NUMBER: {
      return Translations.getInstance().getTranslationEntry("inputErrorNotANumber");
    }
    case RangedAttributeValidationCode.UNKNOWN_ERROR:
    default: {
      return Translations.getInstance().getTranslationEntry("inputErrorGeneral");
    }
  }
};

const getValidationMessageWithNearestNumber = (
  adjacentValues?: IAdjacentValues,
  unitOfMeasure?: string
) => {
  const translations = Translations.getInstance();
  if (!adjacentValues || (!adjacentValues.lowerAdjValue && !adjacentValues.upperAdjValue)) {
    const validationEntry = translations.getTranslationEntry("inputErrorGeneral");
    return validationEntry;
  }

  const { lowerAdjValue, upperAdjValue } = adjacentValues;
  const tokenEngine = new TokenEngine();

  let message;
  let entry;
  if (lowerAdjValue && !upperAdjValue) {
    entry = translations.getTranslationEntry("inputErrorAboveMaximum");
    message = tokenEngine.replace(entry, {
      maximumValue: lowerAdjValue,
      unitOfMeasure: unitOfMeasure,
    });
  } else if (!lowerAdjValue && upperAdjValue) {
    entry = translations.getTranslationEntry("inputErrorBelowMinimum");
    message = tokenEngine.replace(entry, {
      minimumValue: upperAdjValue,
      unitOfMeasure: unitOfMeasure,
    });
  } else {
    entry = translations.getTranslationEntry("inputErrorBetweenRanges");
    message = tokenEngine.replace(entry, {
      lowerValue: lowerAdjValue,
      higherValue: upperAdjValue,
      unitOfMeasure: unitOfMeasure,
    });
  }

  return message;
};

const getValidationMessageForRequiredField = (
  minimum: Decimal,
  maximum?: Decimal,
  unitOfMeasure?: string
) => {
  const entry = Translations.getInstance().getTranslationEntry("inputErrorRequiredField");
  const tokenEngine = new TokenEngine();

  const message = tokenEngine.replace(entry, {
    lowerValue: minimum,
    unitOfMeasure: unitOfMeasure,
    higherValue: maximum,
  });

  return message;
};
